import { Trans, useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import type { Category } from "@arc/contracts";
import { cn } from "@/lib/utils";

export interface InheritCategoriesChecklistProps {
  /** The PARENT facility's attached categories — offered as the new facility's starting set. */
  categories: Category[];
  /** Ids the user has UNTICKED (excluded). Everything not in here is kept — so an empty set = "all inherited". */
  dropped: ReadonlySet<number>;
  onToggle: (id: number) => void;
  /** The parent's name, for the group's accessible label + header copy. */
  parentName: string;
}

/**
 * The "inherit parent categories" checklist shown in the inline create card (facility-features.md): when the target
 * parent has categories, the new facility starts with all of them ticked and the user unticks any to drop. **Purely
 * presentational** — the selection (`dropped`) lives in the create card and there are NO mutations here (unlike the
 * attach/detach-bound {@link CategoryRow} / {@link CategoryPicker}). Each category is an `aria-pressed` toggle button
 * (the repo's checkbox idiom — there is no `<input type=checkbox>` primitive), grouped under a labelled `role="group"`.
 */
export function InheritCategoriesChecklist({ categories, dropped, onToggle, parentName }: InheritCategoriesChecklistProps) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t("facilityManagement.categories.fromParent", { name: parentName })} className="flex flex-col gap-1">
      <p className="text-muted-foreground text-xs">
        <Trans
          i18nKey="facilityManagement.categories.fromParentHint"
          components={{ name: <span className="text-foreground font-medium">{parentName}</span> }}
        />
      </p>
      <div className="flex max-h-40 flex-col gap-0.5 overflow-y-auto">
        {categories.map((c) => {
          const kept = !dropped.has(c.id);
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={kept}
              aria-label={t(kept ? "facilityManagement.categories.included" : "facilityManagement.categories.excluded", { name: c.name })}
              onClick={() => onToggle(c.id)}
              className="hover:bg-muted focus-visible:ring-ring/50 flex items-center gap-2 rounded-md px-1.5 py-1 text-left outline-none transition-colors focus-visible:ring-2"
            >
              <span
                aria-hidden="true"
                className={cn(
                  "grid size-4 shrink-0 place-items-center rounded border transition-colors",
                  kept ? "border-primary bg-primary text-primary-foreground" : "border-border text-transparent",
                )}
              >
                <Check className="size-3" />
              </span>
              <span className={cn("truncate text-sm", !kept && "text-muted-foreground line-through")}>{c.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
